import { useCallback, useState } from 'react'
import { useIntl } from 'react-intl'

import { mixpanelEvents } from '../../../../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../../../../mixpanel/mixpanelTracking'
import {
  ClientRegistration,
  useDeleteClientRegistrationMutation,
  useResendRegistrationEmailMutation,
} from '../../../../../addNewClients/data/addNewClientApi'
import { useAppDispatch } from '../../../../../data/store'
import { setToastContent } from '../../../../data/ltToastAutoActions'

export const useClientRegistrationActions = () => {
  const { formatMessage } = useIntl()
  const dispatch = useAppDispatch()

  const [resendRegistrationEmail] = useResendRegistrationEmailMutation()
  const [deleteClientRegistration] = useDeleteClientRegistrationMutation()

  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<ClientRegistration | null>(null)

  const handleResendPress = useCallback(
    async (reg: ClientRegistration) => {
      track({ event: mixpanelEvents.ADD_NEW_CLIENT_RESEND })
      try {
        await resendRegistrationEmail({ client_registration_id: reg.id }).unwrap()
        dispatch(
          setToastContent({
            text: formatMessage({
              defaultMessage: 'Resent registration link. It will expire in 14 days.',
              description: 'Success toast after resending a client registration invite',
            }),
            id: 'AddNewClientsList-resendSuccess',
            toastType: 'success',
          }),
        )
      } catch {
        dispatch(
          setToastContent({
            text: formatMessage({
              defaultMessage: 'Something went wrong. Please try again.',
              description: 'Generic error toast when resending a client registration fails',
            }),
            id: 'AddNewClientsList-resendError',
            toastType: 'error',
          }),
        )
      }
    },
    [resendRegistrationEmail, dispatch, formatMessage],
  )

  const handleDeletePress = useCallback((reg: ClientRegistration) => {
    track({ event: mixpanelEvents.ADD_NEW_CLIENT_OPEN_DELETE_MODAL })
    setDeleteTarget(reg)
    setShowDeleteConfirmModal(true)
  }, [])

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return

    track({ event: mixpanelEvents.ADD_NEW_CLIENT_CONFIRM_DELETE })
    try {
      await deleteClientRegistration({
        client_registration_id: deleteTarget.id,
      }).unwrap()

      dispatch(
        setToastContent({
          text:
            deleteTarget.registration_status !== 'completed'
              ? formatMessage(
                  {
                    defaultMessage: '{firstName} has been removed',
                    description: 'Success toast after removing a pending or expired invited client',
                  },
                  { firstName: deleteTarget.first_name },
                )
              : formatMessage(
                  {
                    defaultMessage: '{firstName} has been removed and made inactive',
                    description: 'Success toast after removing a registered client',
                  },
                  { firstName: deleteTarget.first_name },
                ),
          id: 'AddNewClientsList-deleteSuccess',
          toastType: 'success',
        }),
      )
    } catch {
      dispatch(
        setToastContent({
          text: formatMessage({
            defaultMessage: 'Something went wrong. Please try again.',
            description: 'Generic error toast when deleting a client registration fails',
          }),
          id: 'AddNewClientsList-deleteError',
          toastType: 'error',
        }),
      )
    } finally {
      setShowDeleteConfirmModal(false)
    }
  }, [deleteTarget, deleteClientRegistration, dispatch, formatMessage])

  const closeDeleteConfirmModal = useCallback(() => {
    setShowDeleteConfirmModal(false)
  }, [])

  return {
    showDeleteConfirmModal,
    deleteTarget,
    handleResendPress,
    handleDeletePress,
    handleConfirmDelete,
    closeDeleteConfirmModal,
  }
}
